import { Injectable } from '@angular/core';
import { Product } from '../../models';
import { Assortment } from '../../services/assortment.service';
import { TranslateService } from '@ngx-translate/core';
import { PimTemplateProperties } from 'gung-list';
import { MetadataService } from '../../services/metadata/metadata.service';

@Injectable({
  providedIn: 'root'
})
export class CloudPimPropertiesHelperService {
  constructor(protected translateService: TranslateService, protected metadataService: MetadataService) { }

  public hasAnyPropertiesToShow(product: Product): boolean {
    const productDetailsTable = this.getPropertiesTable(product);
    for (const productDetail of productDetailsTable) {
      if (!!productDetail && !!productDetail.value && productDetail.value !== '') {
        return true;
      }
    }
    return false;
  }

  public hasGroupsOnProductTemplates(product: Product): boolean {
    const productDetailsTable = this.getPropertiesTable(product);
    for (const productDetail of productDetailsTable) {
      if (!!productDetail && !!productDetail.groupId && productDetail.groupId !== '') {
        return true;
      }
    }
    return false;
  }

  public getPropertiesTable(
    product: Product,
    assortment?: Assortment,
    propertiesToShow?: ('productTemplateProperties' | 'itemProperties' | 'skuProperties')[],
    showEmpty: boolean = false,
    translateBooleans: boolean = false
  ): { title: string; value: any; groupId: string; type?: string; hideFieldName?: boolean }[] {
    const productDetailsTable = [];
    if (product?.extra?.itemProperties || product?.extra?.productTemplateProperties) {
      let itemProperties = [];
      if (propertiesToShow) {
        if (propertiesToShow.includes('itemProperties')) {
          itemProperties.push(...(product.extra.itemProperties || []));
        }
        if (propertiesToShow.includes('productTemplateProperties')) {
          itemProperties.push(...(product.extra.productTemplateProperties || []));
        }
        if (propertiesToShow.includes('skuProperties')) {
          itemProperties.push(...(product.extra.skuProperties || []));
        }
      } else {
        itemProperties = [
          ...(product.extra.itemProperties || []),
          ...(product.extra.productTemplateProperties || [])
        ];
      }
      for (const templateProp of itemProperties) {
        const title = this.getTemplateTitle(templateProp);
        if (
          templateProp?.isDisplayDetails &&
          productDetailsTable.findIndex(prop => prop.title === title) === -1
        ) {
          const raw = this.getProductDetailTable(templateProp, product);
          let resolvedValue: any;
          let hasValue = true;

          if (Array.isArray(raw)) {
            if (raw.length > 0 && (templateProp.type === 'metadata' || templateProp.type === 'symbol') && raw[0].hasOwnProperty('symbol')) {
              templateProp.type = 'symbol';
              resolvedValue = raw.map(v => ({ description: v.description, symbol: `${v.symbol?.thumbnailUrl}` }));
            } else {
              resolvedValue = raw.map(v => {
                const lang = this.translateService.currentLang;
                return v.translateProperties?.[lang]?.name
                  ?? v.translateProperties?.[lang]?.translatedDescription
                  ?? v.description;
              }).join(', ');
            }
          } else if (templateProp.type === 'number' && !isNaN(raw as any)) {
            resolvedValue = raw;
          } else if (typeof raw === 'string' && raw.trim().length !== 0) {
            resolvedValue = raw;
          } else if (typeof raw === 'boolean') {
            resolvedValue = translateBooleans ? this.translateService.instant('BOOLEAN_' + (!!raw)) : raw;
          } else if (raw !== undefined && raw !== null && typeof raw !== 'object') {
            resolvedValue = raw;
          } else if (showEmpty) {
            resolvedValue = '';
          } else {
            hasValue = false;
          }

          if (hasValue) {
            productDetailsTable.push(this.buildEntry(templateProp, resolvedValue));
          }
        }
      }
    }
    if (assortment && (assortment.extra.itemProperties || assortment.extra.productTemplateProperties)) {
      const itemProperties = [
        ...(assortment.extra.itemProperties || []),
        ...(assortment.extra.productTemplateProperties || [])
      ];
      for (const templateProp of itemProperties) {
        const title = this.getTemplateTitle(templateProp);
        if (
          templateProp.isDisplayDetails &&
          productDetailsTable.findIndex(prop => prop.title === title) === -1
        ) {
          const raw = this.getProductDetailTable(templateProp, product);
          let resolvedValue: any;
          let hasValue = true;

          if (Array.isArray(raw)) {
            resolvedValue = raw.map(v => v.description).join(', ');
          } else if (typeof raw === 'string' && raw.trim().length !== 0) {
            resolvedValue = raw;
          } else if (templateProp.type === 'number' && !isNaN(raw as any)) {
            resolvedValue = raw;
          } else if (raw !== undefined && raw !== null) {
            resolvedValue = raw;
          } else if (showEmpty) {
            resolvedValue = '';
          } else {
            hasValue = false;
          }

          if (hasValue) {
            productDetailsTable.push(this.buildEntry(templateProp, resolvedValue));
          }
        }
      }
    }
    return productDetailsTable;
  }

  protected getTemplateTitle(templateProp: PimTemplateProperties): string {
    return templateProp.extra?.translatedName?.[this.translateService.currentLang] || templateProp.translationKey;
  }

  protected buildEntry(templateProp: PimTemplateProperties, value: any): { title: string; value: any; groupId: string; type?: string; hideFieldName?: boolean } {
    return {
      title: this.getTemplateTitle(templateProp),
      value,
      type: templateProp.type,
      groupId: templateProp.groupId,
      hideFieldName: templateProp.hideFieldName
    };
  }

  getProductDetailTable(templateProp: PimTemplateProperties, product: Product): string {
    const fields = templateProp.path.split('.');

    if (!product || !fields || fields.length === 0) {
      return;
    }

    if (fields[0] === 'i18n') {
      fields.splice(1, 0, this.translateService.currentLang);
    }

    return this.getFieldOrPropertyFromProduct(templateProp, product, fields);
  }

  getFieldOrPropertyFromProduct(templateProp: PimTemplateProperties, product: Product, fields: string[]): string {


    // Get field form object if exist
    const getField = (obje: object, field: string) => {
      if (!obje.hasOwnProperty(field)) {
        return null;
      }
      return obje[field];
    };

    // Get property from object
    let obj: any = Object.assign({}, product.extra);
    for (const field of fields) {
      if (!obj) {
        break;
      }
      obj = getField(obj, field);
    }

    if (obj !== undefined && obj !== null) {
      if (templateProp.type === 'text') {
        return obj.description || obj;
      }
      if (templateProp.type === 'metadata') {
        if (obj.hasOwnProperty('description')) {
          // PIM metadata
          const lang = this.translateService.currentLang;
          return obj.translateProperties?.[lang]?.name
            ?? obj.translateProperties?.[lang]?.translatedDescription
            ?? obj.description;
        }
        if (templateProp.metaReference) {
          return this.metadataService.getMetadataValue(
            templateProp.metaReference.table,
            templateProp.metaReference.field,
            obj
          );
        }
        if (templateProp.metadata) {
          if (templateProp.metadata.split('.').length === 2) {
            const splitmeta = templateProp.metadata.split('.');
            const metaTable = splitmeta[0];
            const metaField = splitmeta[1];
            return this.metadataService.getMetadataValue(metaTable, metaField, obj);
          }
          return this.metadataService.getMetadataValue(templateProp.metadata, 'description', obj);
        }
      }
      return obj;
    }

    return null;
  }
}
